import { useNavigate, useLocation } from "react-router-dom";
import { useState, useEffect } from "react";
import { Box, Flex, Text, Paper, ThemeIcon, Badge, Loader, Center, Button, Title } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconCircleCheck, IconBuilding, IconArrowLeft } from "@tabler/icons-react";
import { useUserContext } from "@/context/UserContext";
import trpc from "@/shared/utils/trpc";
import PaymentMethodModal from "./PaymentMethodModal";
import { getIndustryLabel, regionsData } from "@/components/shared/constants/general.const";
import { handleTrpcError } from "@/shared/utils/trpc/handletrpcError.util";
import { successNotification } from "@/shared/utils/successNotification";

export default function MatchSuccessPage() {
  const getRegionLabel = (regionId: string): string => {
    const region = regionsData.regions.find((r) => r.id === regionId);
    return region ? region.name : regionId;
  };
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails } = useUserContext();
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentModalOpened, { open: openPaymentModal, close: closePaymentModal }] = useDisclosure(false);
  const [engagementFeeCredits, setEngagementFeeCredits] = useState(14);

  useEffect(() => {
    trpc.paymentConfig.get.query().then((config) => {
      setEngagementFeeCredits(config.engagementFeeCredits);
    });
  }, []);

  // Get request data from navigation state
  const request = location.state?.request;
  if (!request) {
    return (
      <Center py="xl" mih="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  const matchId = request.id;
  const userCompanyId = userDetails?.company?.id;
  const isRequester = userCompanyId === request.requesterCompanyId;
  const yourCompanyId = userDetails?.company?.gtid || "CM-2025-XXXX";
  const otherCompany = isRequester ? request.receiver : request.requester;
  const matchedCompanyId = otherCompany?.gtid || "CM-2025-XXXX";

  const handleProceedToPayment = () => {
    openPaymentModal();
  };

  const handlePaymentConfirm = async () => {
    setPaymentLoading(true);

    try {
      const result = await trpc.collaboration.payEngagementFee.mutate({ matchId });

      if (result.success) {
        closePaymentModal();

        successNotification({
          title: "Payment Successful",
          message: `${result.creditsCharged} credits charged. ${result.newStatus === "BOTH_PAID" ? "Both parties have paid!" : "Waiting for the other party to pay."}`,
        });

        navigate(`/requests/payment-success/${matchId}`, {
          state: {
            matchId,
            companyId: otherCompany?.id,
            paymentData: {
              transactionId: `TXN-${new Date().getFullYear()}-${Date.now().toString().slice(-8)}`,
              creditsCharged: result.creditsCharged,
              date: new Date().toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              }),
              paymentMethod: "Collaboration Credits",
              newStatus: result.newStatus,
            },
          },
        });
      }
    } catch (error) {
      handleTrpcError(error);
    } finally {
      setPaymentLoading(false);
    }
  };

  return (
    <Box mih="100vh" pb={20}>
      <Box py="sm" visibleFrom="sm">
        <Flex justify="space-between" align="center">
          <Button
            variant="subtle"
            color="slate.4"
            leftSection={<IconArrowLeft size={16} />}
            size="sm"
            onClick={() => navigate("/requests")}
          >
            Back to Interest
          </Button>
        </Flex>
      </Box>
      {/* Header */}
      <Box style={{ borderBottom: "1px solid #E5E7EB" }}>
        <Flex justify="space-between" align="center" px="xl" pb={{ base: "md", sm: "lg" }}>
          <Box>
            <Title order={2} fz={{ base: 20, sm: 24 }} fw={400} mb={4}>
              Match Success
            </Title>
            <Text size="sm" c="slate.5">
              Mutual interest confirmed - proceed to unlock company details
            </Text>
          </Box>
          <Button onClick={handleProceedToPayment} mt={{ base: 0, sm: "md" }} loading={paymentLoading}>
            Proceed to Payment
          </Button>
        </Flex>
      </Box> 

      {/* Main Content */}
      <Box px="xl">
        <Paper radius="lg" p="xl">
          {/* Success Banner */}
          <Paper
            radius="lg"
            p="xl"
            mb="lg"
            style={{
              background:
                "linear-gradient(to right, var(--mantine-color-primary-0), var(--mantine-color-primary-1) 50%, var(--mantine-color-primary-0))",
              border: "1px solid var(--mantine-color-primary-3)",
            }}
          >
            <Flex direction="column" align="center" gap="sm">
              <ThemeIcon size={64} radius={64} color="primary" variant="white">
                <IconCircleCheck size={36} />
              </ThemeIcon>
              <Text fz="lg" fw={400} ta="center">
                It's a Match!
              </Text>
              <Text c="slate.5" fz="sm" ta="center">
                Both companies have expressed mutual interest in collaborating
              </Text>
            </Flex>
          </Paper>

          {/* Collaboration Fee */}
          <Paper withBorder p="xl" radius="lg" mb="lg">
            <Flex justify="space-between" align="flex-start">
              <Box>
                <Text fw={400} mb={4}>
                  Collaboration Fee
                </Text>
                <Text fz="sm" c="slate.5" mb="md">
                  One-time payment for workspace access
                </Text>
                <Flex direction="column" gap="xs">
                  <Flex align="center" gap="xs">
                    <IconCircleCheck size={20} color="var(--mantine-color-slate-5)" />
                    <Text fz="sm" c="slate.5">
                      Full company details revealed
                    </Text>
                  </Flex>
                  <Flex align="center" gap="xs">
                    <IconCircleCheck size={20} color="var(--mantine-color-slate-5)" />
                    <Text fz="sm" c="slate.5">
                      Auto-generated NDA & document sharing
                    </Text>
                  </Flex>
                  <Flex align="center" gap="xs">
                    <IconCircleCheck size={20} color="var(--mantine-color-slate-5)" />
                    <Text fz="sm" c="slate.5">
                      Credits will be refunded if the other party does not pay within 96 hours
                    </Text>
                  </Flex>
                </Flex>
              </Box>
              <Box ta="right">
                <Text fz={28} fw={400} c="slate.8">
                  {engagementFeeCredits} Credits
                </Text>
                <Text fz="sm" c="slate.5">
                  per company
                </Text>
              </Box>
            </Flex>
          </Paper>

          {/* Match Details */}
          <Paper withBorder p="xl" radius="lg" mb="lg">
            <Text fw={400} mb="md">
              Match Details
            </Text>
            <Flex direction={{ base: "column", sm: "row" }} justify="space-between">
              {/* Your Company */}
              <Box>
                <Flex align="center" gap="sm" mb="md">
                  <ThemeIcon size={40} radius="lg" color="primary" variant="light">
                    <IconBuilding size={20} />
                  </ThemeIcon>
                  <Box>
                    <Text fz="xs" c="slate.5">
                      Your Company
                    </Text>
                    <Text fz="sm" fw={500}>
                      {yourCompanyId}
                    </Text>
                  </Box>
                </Flex>

                {/* Your Company - Industry Category */}
                {Array.isArray(userDetails?.company?.industryCategory) && userDetails.company.industryCategory.length > 0 && (
                  <Box mb="xs">
                    <Text fz="xs" c="slate.5" mb={4}>
                      Type of Company
                    </Text>
                    <Text fz="sm">
                      {getIndustryLabel(userDetails.company.industryCategory[0])}
                      {userDetails.company.industryCategory.length > 1 && (
                        <Text span c="slate.5"> +{userDetails.company.industryCategory.length - 1}</Text>
                      )}
                    </Text>
                  </Box>
                )}

                {/* Your Company - Preferred Regions */}
                {userDetails?.company?.preferredCollaborationRegions &&
                  userDetails.company.preferredCollaborationRegions.length > 0 && (
                    <Box>
                      <Text fz="xs" c="slate.5" mb={4}>
                        Preferred Collaboration Regions
                      </Text>
                      <Flex gap="xs" wrap="wrap">
                        {userDetails.company.preferredCollaborationRegions.map((region: string, index: number) => (
                          <Badge
                            key={index}
                            variant="light"
                            color="slate"
                            c="black"
                            py={10}
                            px={8}
                            size="md"
                            fw={400}
                            tt="none"
                          >
                            {getRegionLabel(region)}
                          </Badge>
                        ))}
                      </Flex>
                    </Box>
                  )}
              </Box>

              {/* Matched Company */}
              <Box>
                <Flex align="center" gap="sm" mb="md">
                  <ThemeIcon size={40} radius="lg" color="primary" variant="light">
                    <IconBuilding size={20} />
                  </ThemeIcon>
                  <Box>
                    <Text fz="xs" c="slate.5">
                      Matched Company
                    </Text>
                    <Text fz="sm" fw={500}>
                      {matchedCompanyId}
                    </Text>
                  </Box>
                </Flex>

                {/* Matched Company - Industry Category */}
                {Array.isArray(otherCompany?.industryCategory) && otherCompany.industryCategory.length > 0 && (
                  <Box mb="xs">
                    <Text fz="xs" c="slate.5" mb={4}>
                      Type of Company
                    </Text>
                    <Text fz="sm">
                      {getIndustryLabel(otherCompany.industryCategory[0])}
                      {otherCompany.industryCategory.length > 1 && (
                        <Text span c="slate.5"> +{otherCompany.industryCategory.length - 1}</Text>
                      )}
                    </Text>
                  </Box>
                )}

                {/* Matched Company - Preferred Regions */}
                {otherCompany?.preferredCollaborationRegions &&
                  otherCompany.preferredCollaborationRegions.length > 0 && (
                    <Box>
                      <Text fz="xs" c="slate.5" mb={4}>
                        Preferred Collaboration Regions
                      </Text>
                      <Flex gap="xs" wrap="wrap">
                        {otherCompany.preferredCollaborationRegions.map((region: string, index: number) => (
                          <Badge
                            key={index}
                            variant="light"
                            color="slate"
                            c="black"
                            py={10}
                            px={8}
                            size="md"
                            fw={400}
                            tt="none"
                          >
                            {getRegionLabel(region)}
                          </Badge>
                        ))}
                      </Flex>
                    </Box>
                  )}
              </Box>
            </Flex>
          </Paper>

          {/* Next Steps */}
          <Paper radius="lg" p="lg" bg="#2563EB0D" withBorder style={{ borderColor: "#2563EB33" }}>
            <Text fw={400} mb="md">
              Next Steps
            </Text>
            <Flex direction="column" gap="lg">
              {/* Step 1 */}
              <Flex gap="md" align="flex-start">
                <ThemeIcon size={32} radius={32} color="primary">
                  <Text fz="xs" fw={400} c="white">
                    1
                  </Text>
                </ThemeIcon>
                <Box>
                  <Text fz="sm" fw={400}>
                    Complete Payment
                  </Text>
                  <Text fz="xs" c="slate.5">
                    Pay the collaboration fee to unlock full company details and start the collaboration workspace
                  </Text>
                </Box>
              </Flex>

              {/* Step 2 */}
              <Flex gap="md" align="flex-start">
                <ThemeIcon size={32} radius={32} color="gray" variant="light">
                  <Text fz="xs" fw={400} c="slate.5">
                    2
                  </Text>
                </ThemeIcon>
                <Box>
                  <Text fz="sm" fw={400}>
                    Sign the NDA
                  </Text>
                  <Text fz="xs" c="slate.5">
                    Sign the NDA to get access to a collaboration workspace with document sharing.
                  </Text>
                </Box>
              </Flex>

              {/* Step 3 */}
              <Flex gap="md" align="flex-start">
                <ThemeIcon size={32} radius={32} color="gray" variant="light">
                  <Text fz="xs" fw={400} c="slate.5">
                    3
                  </Text>
                </ThemeIcon>
                <Box>
                  <Text fz="sm" fw={400}>
                    Collaboration Workspace
                  </Text>
                  <Text fz="xs" c="slate.5">
                    Work together through the structured collaboration process with timeline tracking and reports
                  </Text>
                </Box>
              </Flex>
            </Flex>
          </Paper>
        </Paper>
      </Box>

      {/* Payment Method Modal */}
      <PaymentMethodModal
        opened={paymentModalOpened}
        onClose={closePaymentModal}
        onConfirm={handlePaymentConfirm}
        loading={paymentLoading}
        creditsRequired={engagementFeeCredits}
      />
    </Box>
  );
}
